'use client';

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import {
  loginWithPassword,
  verifyLoginOtp,
  checkSession,
  logoutSession,
  type LoginApiUser
} from '@/lib/api/auth.services';

interface User {
  username: string;
  displayName: string;
  role: string;
  userId?: number;
  avatar?: string;
}

export interface LoginResult {
  success: boolean;
  /** When true, show OTP step (credentials were valid and server sent OTP). */
  needsOtp?: boolean;
  message?: string;
  error?: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextValue extends AuthState {
  login: (username: string, password: string) => Promise<LoginResult>;
  verifyOtp: (otp: string) => Promise<{ success: boolean; error?: string }>;
  cancelOtpFlow: () => void;
  logout: () => void;
}

const OTP_PENDING_KEY = 'ptms_otp_pending';
const SESSION_USER_KEY = 'ptms_session_user';

const AuthContext = createContext<AuthContextValue | null>(null);

function mapApiUserToUser(api: LoginApiUser | undefined, fallbackUsername: string): User {
  const name = api?.userName?.trim() || fallbackUsername;
  return {
    username: name,
    displayName: name,
    role: 'User',
    userId: api?.userID,
  };
}

function getSessionUser(): User | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(SESSION_USER_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as User;
  } catch {
    return null;
  }
}

function saveSessionUser(user: User): void {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(SESSION_USER_KEY, JSON.stringify(user));
}

function clearSessionUser(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(SESSION_USER_KEY);
  sessionStorage.removeItem('ptms_auth_token');
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check session via API on mount - backend handles session identification
  useEffect(() => {
    const validateSession = async () => {
      const result = await checkSession();
      if (result.ok) {
        // Session is valid, restore user from sessionStorage
        const savedUser = getSessionUser();
        if (savedUser) {
          setUser(savedUser);
        }
      } else {
        // Session invalid, clear any stored user
        clearSessionUser();
      }
      setIsLoading(false);
    };

    validateSession();
  }, []);

  const cancelOtpFlow = useCallback(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(OTP_PENDING_KEY);
    }
  }, []);

  const login = useCallback(async (username: string, password: string): Promise<LoginResult> => {
    const trimmed = username.trim();
    const result = await loginWithPassword(trimmed, password);

    if (!result.ok) {
      return { success: false, error: result.error };
    }

    if (result.needsOtp) {
      const pending = { 
        userName: result.user?.userName ?? trimmed,
        userID: result.user?.userID
      };
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(OTP_PENDING_KEY, JSON.stringify(pending));
      }
      return {
        success: true,
        needsOtp: true,
        message: result.message,
      };
    }

    // Backend handles session - save user info to sessionStorage
    const mapped = mapApiUserToUser(result.user, trimmed);
    saveSessionUser(mapped);
    if (result.token && typeof window !== 'undefined') {
      sessionStorage.setItem('ptms_auth_token', result.token);
    }
    setUser(mapped);
    return { success: true, needsOtp: false };
  }, []);

  const verifyOtp = useCallback(async (otp: string): Promise<{ success: boolean; error?: string }> => {
    if (typeof window === 'undefined') {
      return { success: false, error: 'Session error' };
    }
    const raw = sessionStorage.getItem(OTP_PENDING_KEY);
    if (!raw) {
      return { success: false, error: 'Session expired. Please sign in again.' };
    }
    let pending: { userName: string; userID?: number };
    try {
      pending = JSON.parse(raw) as { userName: string; userID?: number };
    } catch {
      return { success: false, error: 'Session expired. Please sign in again.' };
    }

    const code = otp.trim();
    if (!code) {
      return { success: false, error: 'Please enter the OTP' };
    }

    const res = await verifyLoginOtp(pending.userName, code);
    if (!res.ok) {
      return { success: false, error: res.error };
    }

    sessionStorage.removeItem(OTP_PENDING_KEY);
    
    // If the OTP response didn't include the user ID, restore it from the pending session
    if (res.user && !res.user.userID && pending.userID) {
      res.user.userID = pending.userID;
    }
    
    // Backend handles session - save user info to sessionStorage
    const mapped = mapApiUserToUser(res.user, pending.userName);
    saveSessionUser(mapped);
    if (res.token && typeof window !== 'undefined') {
      sessionStorage.setItem('ptms_auth_token', res.token);
    }
    setUser(mapped);
    return { success: true };
  }, []);

  const logout = useCallback(async () => {
    // Call API to clear backend session
    await logoutSession();

    // Clear OTP flow and user data
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(OTP_PENDING_KEY);
      clearSessionUser();
    }
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        verifyOtp,
        cancelOtpFlow,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
